import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, formatApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { Database, Loader2, RefreshCw, Trash2, FileText, Activity } from 'lucide-react';
import { toast } from 'sonner';
import BackButton from '../../components/BackButton';
import i18n from 'i18next';

function formatDateTime(iso, lang) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
}

function formatBytes(n) {
    if (!n) return '0 B';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminCvExtractionCachePage() {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [invalidating, setInvalidating] = useState({});

    useEffect(() => { loadStats(true); }, []);

    const loadStats = async (initial = false) => {
        if (!initial) setRefreshing(true);
        try {
            const res = await adminApi.getCvExtractionCacheStats();
            setStats(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRefreshing(false);
            if (initial) setLoading(false);
        }
    };

    const handleClearAll = async () => {
        setClearing(true);
        try {
            const res = await adminApi.clearCvExtractionCache();
            toast.success(t('cv_cache.cleared_toast', { count: res.data.deleted_count }));
            await loadStats();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setClearing(false);
        }
    };

    const handleInvalidate = async (sha) => {
        setInvalidating((m) => ({ ...m, [sha]: true }));
        try {
            const res = await adminApi.invalidateCvExtractionCacheEntry(sha);
            toast.success(t('cv_cache.invalidated_toast', { count: res.data.deleted_count }));
            await loadStats();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setInvalidating((m) => { const c = { ...m }; delete c[sha]; return c; });
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-cv-cache-page">
            <BackButton />

            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] flex items-center gap-2">
                        <Database className="w-6 h-6 text-[#002FA7]" />
                        {t('cv_cache.title')}
                    </h1>
                    <p className="mt-1 text-sm text-[#4B5563]">{t('cv_cache.subtitle')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => loadStats()}
                        disabled={refreshing}
                        data-testid="cv-cache-refresh"
                    >
                        {refreshing
                            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <RefreshCw className="w-4 h-4 mr-2" />}
                        {t('common.refresh')}
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                type="button"
                                variant="outline"
                                className="text-[#B91C1C] border-[#FCA5A5] hover:bg-[#FEF2F2]"
                                data-testid="cv-cache-clear-all-btn"
                                disabled={!stats || stats.entries === 0}
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {t('cv_cache.clear_all')}
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t('cv_cache.clear_all_title')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                    {t('cv_cache.clear_all_description', { count: stats?.entries || 0 })}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={handleClearAll}
                                    disabled={clearing}
                                    data-testid="cv-cache-clear-all-confirm"
                                    className="bg-[#B91C1C] hover:bg-[#991B1B]"
                                >
                                    {clearing
                                        ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        : <Trash2 className="w-4 h-4 mr-2" />}
                                    {t('cv_cache.confirm_clear')}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard
                    testid="cv-cache-kpi-entries"
                    icon={<FileText className="w-5 h-5 text-[#002FA7]" />}
                    label={t('cv_cache.kpi_entries')}
                    value={stats?.entries ?? 0}
                    hint={t('cv_cache.kpi_entries_hint')}
                />
                <KpiCard
                    testid="cv-cache-kpi-hits"
                    icon={<Activity className="w-5 h-5 text-[#059669]" />}
                    label={t('cv_cache.kpi_hits')}
                    value={stats?.total_hits ?? 0}
                    hint={t('cv_cache.kpi_hits_hint')}
                />
                <KpiCard
                    testid="cv-cache-kpi-misses"
                    icon={<Activity className="w-5 h-5 text-[#D97706]" />}
                    label={t('cv_cache.kpi_misses')}
                    value={stats?.total_misses ?? 0}
                    hint={t('cv_cache.kpi_misses_hint')}
                />
                <KpiCard
                    testid="cv-cache-kpi-hitrate"
                    icon={<Database className="w-5 h-5 text-[#7C3AED]" />}
                    label={t('cv_cache.kpi_hit_rate')}
                    value={`${stats?.hit_rate ?? 0}%`}
                    hint={t('cv_cache.kpi_hit_rate_hint')}
                />
            </div>

            <p className="text-xs text-[#6B7280]" data-testid="cv-cache-total-bytes">
                {t('cv_cache.total_bytes', { value: formatBytes(stats?.total_bytes || 0) })}
            </p>

            {/* Top files table */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 md:p-6">
                <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                    {t('cv_cache.top_files_title')}
                </h2>
                {(!stats?.top_files || stats.top_files.length === 0) ? (
                    <p className="text-sm text-[#9CA3AF] text-center py-6">{t('cv_cache.empty')}</p>
                ) : (
                    <div className="overflow-x-auto -mx-4 md:-mx-6">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-semibold text-[#6B7280] uppercase border-b border-[#E5E7EB]">
                                    <th className="px-4 md:px-6 py-2">{t('cv_cache.col_filename')}</th>
                                    <th className="px-2 py-2 hidden md:table-cell">{t('cv_cache.col_sha')}</th>
                                    <th className="px-2 py-2 text-right">{t('cv_cache.col_hits')}</th>
                                    <th className="px-2 py-2 text-right hidden sm:table-cell">{t('cv_cache.col_size')}</th>
                                    <th className="px-2 py-2 hidden lg:table-cell">{t('cv_cache.col_last_used')}</th>
                                    <th className="px-4 md:px-6 py-2 text-right">{t('cv_cache.col_action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.top_files.map((f) => {
                                    const busy = invalidating[f.file_sha256];
                                    return (
                                        <tr key={f.file_sha256} className="border-b border-[#F3F4F6] last:border-0"
                                            data-testid={`cv-cache-row-${f.file_sha256.slice(0, 12)}`}
                                        >
                                            <td className="px-4 md:px-6 py-2 font-medium text-[#0A0A0A] truncate max-w-[220px]" title={f.first_filename}>
                                                {f.first_filename || '—'}
                                            </td>
                                            <td className="px-2 py-2 text-[#4B5563] text-xs font-mono hidden md:table-cell">
                                                {f.file_sha256.slice(0, 12)}…
                                            </td>
                                            <td className="px-2 py-2 text-right font-semibold text-[#059669]">
                                                {f.hits}
                                            </td>
                                            <td className="px-2 py-2 text-right text-[#4B5563] text-xs hidden sm:table-cell">
                                                {formatBytes(f.size_bytes)}
                                            </td>
                                            <td className="px-2 py-2 text-[#6B7280] text-xs hidden lg:table-cell">
                                                {formatDateTime(f.last_used_at, i18n.language)}
                                            </td>
                                            <td className="px-4 md:px-6 py-2 text-right">
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={busy}
                                                    onClick={() => handleInvalidate(f.file_sha256)}
                                                    data-testid={`cv-cache-invalidate-${f.file_sha256.slice(0, 12)}`}
                                                    className="text-[#B91C1C] border-[#FCA5A5] hover:bg-[#FEF2F2]"
                                                >
                                                    {busy
                                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        : <Trash2 className="w-3.5 h-3.5" />}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function KpiCard({ testid, icon, label, value, hint }) {
    return (
        <div
            data-testid={testid}
            className="bg-white border border-[#E5E7EB] rounded-lg p-4"
        >
            <div className="flex items-center gap-2">
                {icon}
                <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">{label}</span>
            </div>
            <p className="mt-2 text-2xl font-bold text-[#0A0A0A] font-['Outfit']">{value}</p>
            {hint && <p className="text-[11px] text-[#9CA3AF] mt-1">{hint}</p>}
        </div>
    );
}
